import { Badge } from '@design/components/badge/Badge';
import { Icon } from '@design/components/icon/Icon';
import { mailTo } from '@helpers';
import { AlertsDropdown } from '@terminal/components/alerts-dropdown/AlertsDropdown';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { config } from '@terminal/config/app.config';
import {
  LAST_USED_SCREENER_URL,
  readLastUsedUrl,
} from '@terminal/hooks/use-persist-last-used-url/lastUsedUrlStorage';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useWatchlistActions } from '@terminal/hooks/use-watchlist-actions/useWatchlistActions';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useAuth } from '@terminal/stores/use-auth/useAuth';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useChatNavigation } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import { resolveScreenerUrl } from '@terminal/views/screener/screener-initialization/resolveScreenerUrl';
import { useScreenerStore } from '@terminal/views/screener/use-screener-store/useScreenerStore';
import { widgetPaths } from '@terminal/views/widgets/widget-utils';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './AppHeader.module.scss';
import { AlertsIcon } from './header-navigation/components/alerts-icon/AlertsIcon';
import {
  HeaderCategory,
  HeaderCategoryItem,
} from './header-navigation/components/category-group/HeaderCategoryGroup';
import { HeaderNavigation } from './header-navigation/HeaderNavigation';
import {
  getActiveNavigationKey,
  isNewTabClick,
  mapNavigationKeyToPathname,
  navigationKeys,
} from './utils/navigation-utils';

export const FEEDBACK_SUPPORT_EMAIL = 'support@reflexivity.com';

const getScreenerIdForNavigation = (openInNewTab: boolean) => {
  const { activeSavedScreen, draftScreenFilterGroups } =
    useScreenerStore.getState();

  if (!openInNewTab && !activeSavedScreen && draftScreenFilterGroups) {
    return undefined;
  }

  const lastUsedScreenerUrl = readLastUsedUrl(LAST_USED_SCREENER_URL);

  return lastUsedScreenerUrl
    ? resolveScreenerUrl(lastUsedScreenerUrl)?.savedScreen?.id
    : undefined;
};

export const AppHeader = () => {
  const { t } = useTranslation(['screener', 'common']);
  const navigate = useNavigate();
  const { setQueryParams } = useQueryParams();
  const {
    trackDashboardOpen,
    trackChartOpen,
    trackSearchOpen,
    trackWatchlistOpen,
    trackNewsOpen,
    trackInsightsOpen,
    trackScenarioOpen,
    trackScreenerOpen,
    trackInsightFeedOpen,
    trackEventPageOpen,
  } = usePosthogEvents();

  const { logout } = useAuth();
  const colorScheme = useThemeStore(state => state.colorScheme);
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const isGraphEnabled = useFlags(state =>
    state.getFeatureFlagState('KG-772-kg-graph-explorer')
  );

  const { navigateToWatchlist } = useWatchlistActions();
  const { navigateToConversation, setAlfredTrackingSource } =
    useChatNavigation();

  const {
    unreadConversations,
    completeUnreadConversationCount,
    errorUnreadConversationCount,
  } = useChat(state => ({
    unreadConversations: state.getUnreadConversations(),
    completeUnreadConversationCount:
      state.getCompleteUnreadConversations().length,
    errorUnreadConversationCount: state.getErrorUnreadConversations().length,
  }));

  const isProUser = !isBasicUser;
  const badgeLabel =
    completeUnreadConversationCount > 9
      ? '+9'
      : completeUnreadConversationCount;

  const isProcessing = unreadConversations.some(c => c.status === 'Processing');

  const defaultNavigationHandler = (categoryItem: HeaderCategoryItem) => {
    navigate(mapNavigationKeyToPathname[categoryItem.key]);
  };

  const newTabNavigationHandler = (categoryItem: HeaderCategoryItem) =>
    window.open(
      mapNavigationKeyToPathname[categoryItem.key],
      '_blank',
      'noopener,noreferrer'
    );

  const showNotifications =
    !!completeUnreadConversationCount || !!errorUnreadConversationCount;

  const alfredItem: HeaderCategoryItem = {
    label: t('common:sideNavigation.alfred'),
    pillStyle: true,
    isAlfred: true,
    key: navigationKeys.alfred,
    icon: <Icon iconName="Alfred" size={20} />,
    onClick: (item, e) => {
      setAlfredTrackingSource(POSTHOG_TRACKING_SOURCES.NAVIGATION_BAR);
      isNewTabClick(e)
        ? newTabNavigationHandler(item)
        : defaultNavigationHandler(item);
    },
    isProcessing: isProcessing,
    isFinished: !!completeUnreadConversationCount,
    notification: !isProUser && showNotifications && (
      <>
        {!!errorUnreadConversationCount && (
          <Badge
            variant="numerical"
            size="small"
            label="!"
            className={clsx(styles.errorBadge, {
              [styles.withCounterBadge]: !!completeUnreadConversationCount,
            })}
          />
        )}
        {!!completeUnreadConversationCount && (
          <Badge
            variant="numerical"
            size="small"
            label={badgeLabel}
            aria-label={t('common:notifications.count', {
              count: completeUnreadConversationCount,
            })}
            className={clsx(
              styles.counterBadge,
              !!errorUnreadConversationCount && styles.withError
            )}
          />
        )}
      </>
    ),
    unreadConversations: unreadConversations,
    onConversationClick: (conversationId: string) => {
      navigateToConversation(conversationId, {
        trackingSource: POSTHOG_TRACKING_SOURCES.NAVIGATION_BAR,
      });
    },
  };
  const graphItems: HeaderCategoryItem[] = isGraphEnabled
    ? [
        {
          label: t('common:sideNavigation.graph'),
          key: navigationKeys.graph,
          onClick: (item, e) => {
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
      ]
    : [];

  const basicCategories: HeaderCategory[] = [
    {
      id: 'menu',
      items: [
        {
          label: t('common:sideNavigation.dashboard'),
          key: navigationKeys.dashboard,
          onClick: (item, e) => {
            trackDashboardOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.watchlist'),
          key: navigationKeys.watchlist,
          onClick: (_, e) => {
            const watchlistId = useWatchlist.getState().lists[0]?.id;
            trackWatchlistOpen('nav', watchlistId);
            navigateToWatchlist({
              id: watchlistId,
              skipFallback: true,
              openInNewTab: isNewTabClick(e),
            });
          },
        },
        {
          label: t('common:sideNavigation.screener'),
          key: navigationKeys.screener,
          onClick: (_, e) => {
            const openInNewTab = isNewTabClick(e);
            trackScreenerOpen('nav', getScreenerIdForNavigation(openInNewTab));

            if (openInNewTab) {
              window.open(appPaths.screener, '_blank', 'noopener,noreferrer');
            } else {
              navigate(appPaths.screener);
            }
          },
        },
        {
          label: t('common:sideNavigation.explore'),
          key: navigationKeys.insights,
          onClick: (item, e) => {
            trackInsightsOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.news'),
          key: navigationKeys.news,
          onClick: (item, e) => {
            trackNewsOpen({
              source: POSTHOG_TRACKING_SOURCES.NAVIGATION_BAR,
            });
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.chart'),
          key: navigationKeys.chart,
          onClick: (item, e) => {
            trackChartOpen(POSTHOG_TRACKING_SOURCES.NAVIGATION_BAR);
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        ...graphItems,
        alfredItem,
      ],
    },
  ];

  const categories: HeaderCategory[] = [
    {
      id: 'menu',
      items: [
        {
          label: t('common:sideNavigation.dashboard'),
          key: navigationKeys.dashboard,
          onClick: (item, e) => {
            trackDashboardOpen('nav');
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.insightFeed'),
          key: navigationKeys.insightFeed,
          onClick: (
            item: HeaderCategoryItem,
            e: React.MouseEvent<Element, MouseEvent> | undefined
          ) => {
            trackInsightFeedOpen(POSTHOG_TRACKING_SOURCES.NAVIGATION_BAR);
            isNewTabClick(e)
              ? newTabNavigationHandler(item)
              : defaultNavigationHandler(item);
          },
        },
        {
          label: t('common:sideNavigation.watchlist'),
          key: navigationKeys.watchlist,
          onClick: (_, e) => {
            const watchlistId = useWatchlist.getState().lists[0]?.id;
            trackWatchlistOpen('nav', watchlistId);
            navigateToWatchlist({
              id: watchlistId,
              skipFallback: true,
              openInNewTab: isNewTabClick(e),
            });
          },
        },
        ...graphItems,
        {
          label: t('common:sideNavigation.more'),
          key: navigationKeys.more,
          icon: (
            <div className={styles.moreDropdownContainer}>
              <div className={styles.moreDropdownLabel}>
                {t('common:sideNavigation.more')}
              </div>
              <Icon iconName="ChevronLightDown" size={12} />
            </div>
          ),
          onClick: () => {},
          dropdownItems: [
            [
              {
                label: t('common:sideNavigation.chart'),
                key: navigationKeys.chart,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackChartOpen(POSTHOG_TRACKING_SOURCES.NAVIGATION_BAR);
                  const navItem = {
                    key: navigationKeys.chart,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
              {
                label: t('common:sideNavigation.screener'),
                key: navigationKeys.screener,
                onClick: (e?: Event | React.MouseEvent) => {
                  const openInNewTab = isNewTabClick(
                    e as React.MouseEvent | undefined
                  );
                  trackScreenerOpen(
                    'nav',
                    getScreenerIdForNavigation(openInNewTab)
                  );
                  const navItem = {
                    key: navigationKeys.screener,
                  } as HeaderCategoryItem;
                  openInNewTab
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
              {
                label: t('common:sideNavigation.news'),
                key: navigationKeys.news,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackNewsOpen({
                    source: POSTHOG_TRACKING_SOURCES.NAVIGATION_BAR,
                  });
                  const navItem = {
                    key: navigationKeys.news,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },

              {
                label: t('common:sideNavigation.events'),
                key: navigationKeys.events,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackEventPageOpen('nav');
                  const navItem = {
                    key: navigationKeys.events,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
              {
                label: t('common:sideNavigation.scenario'),
                key: navigationKeys.scenario,
                onClick: (e?: Event | React.MouseEvent) => {
                  trackScenarioOpen('nav');
                  const navItem = {
                    key: navigationKeys.scenario,
                  } as HeaderCategoryItem;
                  isNewTabClick(e as React.MouseEvent | undefined)
                    ? newTabNavigationHandler(navItem)
                    : defaultNavigationHandler(navItem);
                },
              },
            ],
          ],
        },
        alfredItem,
      ],
    },
  ];

  const activeCategories = isBasicUser ? basicCategories : categories;

  const settingsAccountItems = [
    {
      key: navigationKeys.settings,
      label: t('common:sideNavigation.account'),
      onClick: () =>
        navigate(mapNavigationKeyToPathname[navigationKeys.settings]),
    },
    {
      key: navigationKeys.subscription,
      label: t('common:sideNavigation.subscription'),
      onClick: () =>
        navigate(mapNavigationKeyToPathname[navigationKeys.subscription]),
    },
    {
      key: navigationKeys.portfolio,
      label: t('common:sideNavigation.portfolio'),
      onClick: () =>
        navigate(mapNavigationKeyToPathname[navigationKeys.portfolio]),
    },
    {
      key: navigationKeys.appearance,
      label: t('common:sideNavigation.appearance'),
      onClick: () =>
        navigate(mapNavigationKeyToPathname[navigationKeys.appearance]),
    },
    ...(isProUser
      ? [
          {
            key: navigationKeys.following,
            label: t('common:sideNavigation.following'),
            onClick: () =>
              navigate(mapNavigationKeyToPathname[navigationKeys.following]),
          },
        ]
      : []),
  ];

  const settingsSupportItems = [
    {
      label: t('common:sideNavigation.feedback'),
      key: navigationKeys.feedback,
      iconName: 'Feedback' as const,
      onClick: () => {
        const subject = t('common:sideNavigation.terminalFeedback');
        window.location.href = mailTo(FEEDBACK_SUPPORT_EMAIL, {
          subject,
        });
      },
    },
    ...(isProUser
      ? [
          {
            label: t('common:sideNavigation.onboarding'),
            key: navigationKeys.onboarding,
            iconName: 'Headset' as const,
            onClick: () => {
              window.open(
                config.urls.onboardingUrl,
                '_blank',
                'noopener,noreferrer'
              );
            },
          },
        ]
      : []),
  ];

  const categoriesOptions: HeaderCategory[] = [
    {
      id: 'options',
      items: [
        ...(isProUser
          ? [
              {
                key: navigationKeys.alerts,
                label: t('common:sideNavigation.alerts'),
                icon: <AlertsIcon />,
                showTooltip: true,
                onClick: () => {},
                customDropdownContent: (onClose: () => void) => (
                  <AlertsDropdown onClose={onClose} />
                ),
              },
            ]
          : []),
        {
          key: navigationKeys.help,
          label: t('common:sideNavigation.help'),
          icon: <Icon iconName="QuestionMarkCircleLine" size={24} />,
          showTooltip: true,
          onClick: () => {
            window.zE('webWidget', 'open');
            window.zE('webWidget', 'show');
          },
        },
        {
          key: navigationKeys.settings,
          label: t('common:sideNavigation.settings'),
          icon: <Icon iconName="Settings" size={24} />,
          showTooltip: true,
          onClick: () => {},
          dropdownItems: [
            settingsAccountItems,
            settingsSupportItems,
            [
              {
                label: t('common:sideNavigation.signOut'),
                key: navigationKeys.signOut,
                iconName: 'SignOut',
                onClick: () => logout(),
              },
            ],
          ],
        },
      ],
    },
  ];

  return (
    <HeaderNavigation
      className={styles.styledHeaderNavigation}
      activeKey={getActiveNavigationKey([
        ...activeCategories,
        ...categoriesOptions,
      ])}
      colorScheme={colorScheme}
      onLogoClick={() => {
        navigate(appPaths.base);
      }}
      onSearchClick={() => {
        trackSearchOpen('nav');
        setQueryParams({
          [queryKeys.widget]: widgetPaths.searchAsset,
        });
      }}
      categories={activeCategories}
      categoriesOptions={categoriesOptions}
    />
  );
};
